import { useTranslate } from "@opensea/next-translate"
import { formatDistanceToNow } from "date-fns"
import { BigNumber } from "bignumber.js"
import React from "react"
import { graphql, useFragment } from "react-relay"
import { List, Tooltip } from "@opensea/ui-kit/client"
import {
  CheckCircleFilled,
  CancelFilled,
  ArrowForward,
} from "@opensea/ui-kit/icons"
import { Flex } from "@opensea/ui-kit"
import type { Swap_metadata$key } from "@/lib/graphql/__generated__/Swap_metadata.graphql"
import type { NumberInput } from "@/lib/helpers/numberUtils"
import { bn } from "@/lib/helpers/numberUtils"
import { TokenAmount } from "@/components/common/TokenAmount"
import { useChains } from "@/hooks/useChains"
import { ChainMedia } from "@/components/common/media/ChainMedia.react"
import { useConnectedChain } from "@/providers/Wallet/selectors"
import { getEthereumChain } from "@/lib/helpers/chainUtils"
import type { TxState } from "../types"
import { TxItem } from "../components/TxItem.react"
import { useTransactionUrl } from "../hooks/useTransactionUrl"

type SwapProps = {
  metadata: Swap_metadata$key
  state: TxState
  transactionHash: string | undefined
  nonce?: number
  createdDate?: string
}

export function Swap({
  metadata: metadataKey,
  state,
  transactionHash,
  nonce,
  createdDate,
}: SwapProps) {
  const t = useTranslate("transactions")
  const { getChainName, getNativeCurrencySymbol } = useChains()

  const connectedChain = useConnectedChain()
  const swapUrl = useTransactionUrl(transactionHash)

  const { sentAssetQuantities, receivedAssetQuantities, bridgeMetadata } =
    useFragment<Swap_metadata$key>(
      graphql`
        fragment Swap_metadata on TransactionMetadataType {
          bridgeMetadata {
            bridgeStatus
            expectedBridgeCompletionTime
            aggregator
          }
          sentAssetQuantities {
            asset {
              decimals
              symbol
              chain {
                identifier
              }
            }
            quantity
          }
          receivedAssetQuantities {
            asset {
              decimals
              symbol
              chain {
                identifier
              }
            }
            quantity
          }
        }
      `,
      metadataKey,
    )

  const isBridge = Boolean(bridgeMetadata)

  const bridgeUrl =
    bridgeMetadata && bridgeMetadata.aggregator === "lifi"
      ? `https://explorer.li.fi/tx/${transactionHash}`
      : `https://www.socketscan.io/tx/${transactionHash}`
  // Only one asset is sent in a swap transaction
  const fromAsset = sentAssetQuantities.length
    ? sentAssetQuantities[0].asset
    : undefined

  const fromSymbol =
    fromAsset?.symbol ?? getNativeCurrencySymbol(connectedChain || "ETHEREUM")
  const fromChain =
    fromAsset?.chain.identifier ?? connectedChain ?? getEthereumChain()
  const fromDecimals = fromAsset?.decimals
  const fromQuantity = bn(
    BigNumber.sum(...sentAssetQuantities.map(aq => aq.quantity)),
    fromDecimals,
  )

  // Only one asset is received in a swap transaction
  const toAsset = receivedAssetQuantities.length
    ? receivedAssetQuantities[0].asset
    : undefined

  const toSymbol = toAsset?.symbol ?? "ETH"
  const toChain =
    toAsset?.chain.identifier ?? connectedChain ?? getEthereumChain()
  const toDecimals = toAsset?.decimals
  const toQuantity = bn(
    BigNumber.sum(...receivedAssetQuantities.map(aq => aq.quantity)),
    toDecimals,
  )

  const isComplete = transactionHash && state !== "PENDING"
  const isPending = state === "PENDING"

  const getTitle = () => {
    if (isBridge) {
      return t("txItem.swap.title.multiChain", "Bridge deposit")
    }
    return t("txItem.swap.title.singleChain", "Swap")
  }

  const getDescription = () => {
    if (isBridge) {
      return t("txItem.swap.description.multiChain", "to {{toChain}}", {
        toChain: getChainName(toChain),
      })
    }
    return (
      <Flex className="items-center gap-1">
        {fromSymbol} <ArrowForward size={12} /> {toSymbol}
      </Flex>
    )
  }

  const getStateIcon = () => {
    if (bridgeMetadata?.bridgeStatus === "PENDING") {
      return (
        <Tooltip
          content={
            bridgeMetadata.expectedBridgeCompletionTime
              ? t(
                  "txItem.swap.pendingTooltipWithEstimatedArrival",
                  "Deposit complete. Estimated arrival: {{timeEstimate}}",
                  {
                    timeEstimate: formatDistanceToNow(
                      new Date(bridgeMetadata.expectedBridgeCompletionTime),
                    ),
                  },
                )
              : t(
                  "txItem.swap.pendingTooltip",
                  "Deposit complete. Funds will arrive soon.",
                )
          }
        >
          <div className="absolute -bottom-0.5 -right-1 rounded-full bg-elevation-2">
            <CheckCircleFilled fill="warning" />
          </div>
        </Tooltip>
      )
    }
    if (bridgeMetadata?.bridgeStatus === "FAILED") {
      return (
        <CancelFilled
          className="absolute -bottom-0.5 -right-1 rounded-full bg-elevation-2"
          fill="accent-red"
        />
      )
    }
    if (isComplete) {
      return state === "SUCCESSFUL" ? (
        <CheckCircleFilled
          className="absolute -bottom-0.5 -right-1 rounded-full bg-elevation-2"
          fill="green-2"
        />
      ) : (
        <CancelFilled
          className="absolute -bottom-0.5 -right-1 rounded-full bg-elevation-2"
          fill="accent-red"
        />
      )
    }
  }
  const transactionUrl = isBridge ? bridgeUrl : swapUrl
  return (
    <TxItem href={isPending ? undefined : transactionUrl} target="_blank">
      <List.Item.Avatar className="relative overflow-visible" size={60}>
        <ChainMedia chain={fromChain} />
        {getStateIcon()}
      </List.Item.Avatar>
      <TxItem.Content>
        <TxItem.Title>{getTitle()}</TxItem.Title>
        <TxItem.Description>{getDescription()}</TxItem.Description>
      </TxItem.Content>
      {isPending ? (
        <TxItem.Pending
          createdDate={createdDate}
          href={transactionUrl}
          nonce={nonce}
        />
      ) : (
        <SwapTxItemValue
          fromQuantity={fromQuantity}
          fromSymbol={fromSymbol}
          toQuantity={toQuantity}
          toSymbol={toSymbol}
        />
      )}
    </TxItem>
  )
}

type SwapTxItemValueProps = {
  fromSymbol: string
  fromQuantity: NumberInput
  toSymbol: string
  toQuantity: NumberInput
}

function SwapTxItemValue({
  fromSymbol,
  fromQuantity,
  toSymbol,
  toQuantity,
}: SwapTxItemValueProps) {
  return (
    <List.Item.Side>
      <List.Item.Title className="text-md">
        -<TokenAmount amount={fromQuantity} symbol={fromSymbol} />
      </List.Item.Title>
      {!bn(toQuantity).isNaN() && (
        <List.Item.Description className="text-sm">
          +<TokenAmount amount={toQuantity} symbol={toSymbol} />
        </List.Item.Description>
      )}
    </List.Item.Side>
  )
}
